import { Button } from "@acme/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@acme/ui/sheet";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { ArrowLeft, Menu } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EntityDetailTabs from "~/common/components/entity-detail-tabs.js";
import { api } from "~/common/utils/trpc.js";
import type { FileRoutesByTo } from "~/routeTree.gen.js";
import { PlayerLevelBadge } from "../_components/-player-level-badge.js";
import { PlayerThBadge } from "../_components/-player-th-badge.js";
import { PlayerTrophiesBadge } from "../_components/-player-trophies-badge.js";

export const Route = createFileRoute("/_authed/my-players/$tag")({
  component: PlayerTagLayout,
});

const TAB_ROUTES = [
  { value: "dashboard", to: "/my-players/$tag", labelKey: "app.playerDetail.tabs.dashboard", end: true },
  { value: "clans", to: "/my-players/$tag/clans", labelKey: "app.playerDetail.tabs.clans", end: false },
  {
    value: "clan-search",
    to: "/my-players/$tag/clan-search/filters",
    labelKey: "app.playerDetail.tabs.clanSearch",
    end: false,
  },
  {
    value: "social-media",
    to: "/my-players/$tag/social-media",
    labelKey: "app.playerDetail.tabs.socialMedia",
    end: false,
  },
  {
    value: "notifications",
    to: "/my-players/$tag/notifications",
    labelKey: "app.playerDetail.tabs.notifications",
    end: false,
  },
] as const satisfies {
  value: string;
  to: keyof FileRoutesByTo;
  labelKey: string;
  end: boolean;
}[];

function getActiveTab(pathname: string, tag: string) {
  if (
    pathname.includes(`/my-players/${tag}/clan-search`) ||
    pathname.includes(`/my-players/${tag}/search-filters`) ||
    pathname.includes(`/my-players/${tag}/search-filter`) ||
    pathname.includes(`/my-players/${tag}/matchs`)
  ) {
    return "clan-search";
  }
  for (let i = TAB_ROUTES.length - 1; i >= 0; i--) {
    const { value, to, end } = TAB_ROUTES[i];
    const path = to.replace("$tag", tag);
    if (end ? pathname === path || pathname === path + "/" : pathname.startsWith(path)) {
      return value;
    }
  }
  return "dashboard";
}

function PlayerDetailTabs({
  tag,
  className,
  layout = "horizontal",
  onNavigate,
}: {
  readonly tag: string;
  readonly className?: string;
  readonly layout?: "horizontal" | "vertical";
  readonly onNavigate?: () => void;
}) {
  const location = useLocation();
  const activeTab = getActiveTab(location.pathname, tag);

  return (
    <EntityDetailTabs
      activeTab={activeTab}
      className={className}
      layout={layout}
      onNavigate={onNavigate}
      tabs={TAB_ROUTES}
      tag={tag}
    />
  );
}

function PlayerTagLayout() {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const [sheetOpen, setSheetOpen] = useState(false);

  const overviewQueryOptions = api.player.getOverviewItemByTag.queryOptions(
    { playerTag: tag },
    { enabled: tag.length > 0 }
  );
  const { data: overview } = useQuery(overviewQueryOptions);

  const displayTag = tag.startsWith("#") ? tag : `#${tag}`;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4 mb-6">
        <Link
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground shrink-0"
          to="/my-players">
          <ArrowLeft className="h-4 w-4" />
          {t("app.playerDetail.backToPlayers")}
        </Link>
        <nav aria-label={t("app.playerDetail.sectionsAria")} className="flex items-center min-w-0">
          <div className="hidden md:block">
            <PlayerDetailTabs tag={tag} />
          </div>
          <Sheet onOpenChange={setSheetOpen} open={sheetOpen}>
            <SheetTrigger asChild>
              <Button aria-label={t("app.playerDetail.openMenu")} className="md:hidden shrink-0" size="icon" variant="ghost">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[min(100vw-2rem,18rem)]" side="right">
              <SheetHeader>
                <SheetTitle>{t("app.playerDetail.sectionsMenu")}</SheetTitle>
              </SheetHeader>
              <PlayerDetailTabs
                className="pt-2"
                layout="vertical"
                onNavigate={() => setSheetOpen(false)}
                tag={tag}
              />
            </SheetContent>
          </Sheet>
        </nav>
      </div>

      {overview != null ? (
        <div className="mb-6 flex flex-col gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold truncate">{overview.name}</h1>
          <p className="text-sm text-muted-foreground font-mono">{displayTag}</p>
          <div className="flex flex-wrap gap-2">
            <PlayerThBadge level={overview.townHallLevel} />
            <PlayerLevelBadge level={overview.expLevel} />
            <PlayerTrophiesBadge leagueId={overview.league} trophies={overview.trophies} />
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-muted-foreground">{displayTag}</h1>
        </div>
      )}

      <Outlet />
    </div>
  );
}
